import api from "api/axios";

export interface CoverLetterDocument {
  id: number;
  owner_type: string;
  owner_id: number;
  title: string;
  content: string;
  category_id: number | null;
  last_score: { overall_score: number | null; top_priority_fix: string | null } | null;
  last_enhanced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CoverLetterDocumentCreate {
  title?: string;
  content?: string;
  category_id?: number | null;
}

export interface CoverLetterDocumentUpdate extends CoverLetterDocumentCreate {}

export interface CoverLetterEnhanceResult {
  content: string;
  overall_score: number;
  critique: string;
  top_priority_fix: string;
}

export interface LastCoverLetterEnhancement {
  job_description: string;
  job_title: string;
  company_name: string;
  cv_id: number | null;
  result: CoverLetterEnhanceResult | null;
  logs: any[];
  enhanced_at: string;
  provider?: "openai" | "deepseek";
}

/** Returns the correct API base path based on the authenticated user type. */
export function coverLetterApiBase(): string {
  return localStorage.getItem("authUserType") === "admin"
    ? "/admin/cover-letter"
    : "/employee/cover-letter";
}

export async function listCoverLetterDocuments(): Promise<CoverLetterDocument[]> {
  const res = await api.get(`${coverLetterApiBase()}/documents`);
  return res.data;
}

export async function createCoverLetterDocument(payload: CoverLetterDocumentCreate): Promise<CoverLetterDocument> {
  const res = await api.post(`${coverLetterApiBase()}/documents`, payload);
  return res.data;
}

export interface CoverLetterDocumentCreateAsEmployee extends CoverLetterDocumentCreate {
  employee_code: string;
}

/** Admin-only: saves a cover letter directly into a Technical Manager's own
 * collection (e.g. an enhanced cover letter produced while admin ran Job Board
 * on that employee's behalf). */
export async function createEmployeeCoverLetterDocumentAsAdmin(
  payload: CoverLetterDocumentCreateAsEmployee
): Promise<CoverLetterDocument> {
  const res = await api.post(`/admin/cover-letter/employee-documents`, payload);
  return res.data;
}

export async function getCoverLetterDocument(docId: number): Promise<CoverLetterDocument> {
  const res = await api.get(`${coverLetterApiBase()}/documents/${docId}`);
  return res.data;
}

export async function getEmployeeCoverLetterDocumentAsAdmin(docId: number): Promise<CoverLetterDocument> {
  const res = await api.get(`/admin/cover-letter/employee-documents/${docId}`);
  return res.data;
}

export async function updateCoverLetterDocument(docId: number, payload: CoverLetterDocumentUpdate): Promise<CoverLetterDocument> {
  const res = await api.patch(`${coverLetterApiBase()}/documents/${docId}`, payload);
  return res.data;
}

export async function deleteCoverLetterDocument(docId: number): Promise<void> {
  await api.delete(`${coverLetterApiBase()}/documents/${docId}`);
}

export async function enhanceCoverLetterPreview(
  docId: number,
  jobDescription: string,
  cvId?: number | null
): Promise<CoverLetterEnhanceResult> {
  const res = await api.post(`${coverLetterApiBase()}/documents/${docId}/enhance-preview`, {
    job_description: jobDescription,
    cv_id: cvId ?? null,
  });
  return res.data;
}

export async function getLastCoverLetterEnhancement(docId: number): Promise<LastCoverLetterEnhancement> {
  const res = await api.get(`${coverLetterApiBase()}/documents/${docId}/last-enhancement`);
  return res.data;
}
